import { ButtonV2, DataGrid, DataGridColumn, DataGridRef, Sort, SortOrder } from '@cimplico/components';
import {
    GenericWorksheet,
    ReconciliationWorksheet,
    WorksheetEntryV2,
    WorksheetStatusEnum,
    WorksheetTypeEnum,
} from '../../../types/worksheet';
import { ROW_SIZES } from '../../../constants';
import EntryRow from './EntryRow';
import EntriesSubtotal from './EntriesSubtotal';
import { useEffect, useMemo, useRef, useState } from 'react';
import NewOrEditEntryRow from './NewOrEditEntryRow';
import { useWorkpaperContext } from '../../../providers/WorkpaperProvider';
import { NatureEnum } from '../../../types/nature';
import { CreateWorksheetEntryRequest, useCreateWorksheetEntry } from '../../../queries/use-create-worksheet-entry';
import { useDeleteWorksheetEntries } from '../../../queries/use-delete-worksheet-entries';
import NoEntriesMessage from './NoEntriesMessage';
import { UpdateWorksheetEntriesRequest, useUpdateWorksheetEntries } from '../../../queries/use-update-worksheet-entries';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { WorksheetEntry } from '../../../types/worksheet-entry';
import DocumentBrowser from '../../../components/document-browser/DocumentBrowser';
import { parseToCents } from '../../new-workpaper/utils';
import { toastNotification } from '../../../components/toast/Toast';

type EntriesTableProps = {
    entries: WorksheetEntryV2[];
    worksheet: ReconciliationWorksheet | GenericWorksheet;
    isSorting: boolean;
    onSort: (sort: string) => void;
};

const columns: DataGridColumn[] = [
    {
        header: 'Date',
        width: '1fr',
        alignment: 'left',
    },
    {
        header: 'Reference',
        width: '0.5fr',
        alignment: 'left',
    },
    {
        header: 'Description',
        width: '2fr',
        alignment: 'left',
    },
    {
        header: 'Attachments',
        width: '1fr',
        alignment: 'center',
    },
];

const genColumns: DataGridColumn[] = [
    ...columns,
    {
        header: '',
        width: '0fr',
        alignment: 'right',
    },
];

const recColumns: DataGridColumn[] = [
    ...columns,
    {
        header: 'Amount',
        width: '2fr',
        alignment: 'right',
    },
];

const baseSortOptions = [
    { id: 'date', label: 'Date' },
    { id: 'createdAt', label: 'Created At' },
    { id: 'reference', label: 'Reference' },
    { id: 'description', label: 'Description' },
    { id: 'attachments', label: 'File Count' },
];

type NewEntryPrefill = {
    date?: string;
    description?: string;
    amount?: number;
    nature?: NatureEnum;
};

function EntriesTable({ entries, worksheet, onSort, isSorting }: EntriesTableProps): JSX.Element {
    const [newEntry, setNewEntry] = useState<NewEntryPrefill | undefined>();
    const [selectedEntries, setSelectedEntries] = useState<string[]>([]);
    const [isDragging, setDragging] = useState(false);
    const [sortedEntries, setSortedEntries] = useState(entries);
    const [editingEntryId, setEditingEntryId] = useState<string>();

    const [viewingAttachmentsEntryId, setViewingAttachmentsEntryId] = useState<string>();

    const { readOnly } = useWorkpaperContext();

    const sortOptions = useMemo(() => {
        if (worksheet.type === WorksheetTypeEnum.RECONCILIATION) {
            return [...baseSortOptions, { id: 'amount', label: 'Amount' }];
        }
        return baseSortOptions;
    }, [worksheet.type]);

    // A user can change the sequence and update an entry a the same time. This is to avoid two api calls
    const [queuedSequenceChanges, setQueuedSequenceChanges] = useState<{ id: string; fields: { sequence: number } }[]>([]);

    const tableRef = useRef<DataGridRef>(null);

    const createEntryQuery = useCreateWorksheetEntry(worksheet.id);
    const deleteEntriesQuery = useDeleteWorksheetEntries(worksheet.id);
    const updateEntriesQuery = useUpdateWorksheetEntries(worksheet.id);

    // This will be 1 when the user clicks the save button (The mutation is running in the NewEntry component)
    const { periodEnd } = useWorkpaperContext();

    const canDeleteEntries = entries.filter((e) => selectedEntries.includes(e.id) && !e.source);

    useEffect(() => {
        setSortedEntries(entries);
    }, [entries]);

    function handleReorder(sortedIds: string[]): void {
        const sorted = [...entries].sort((a, b) => sortedIds.indexOf(a.id) - sortedIds.indexOf(b.id));
        setSortedEntries(sorted);
    }

    function handleDrop(): void {
        const prevEntryIds = entries.map((l) => l.id);
        const sortedIds = sortedEntries.map((e) => e.id);
        const updatedEntries = [];

        for (const entryId of sortedIds) {
            if (prevEntryIds.indexOf(entryId) !== sortedIds.indexOf(entryId)) {
                updatedEntries.push({ id: entryId, fields: { sequence: sortedIds.indexOf(entryId) + 1 } });
            }
        }

        if (!editingEntryId && updatedEntries.length !== 0) {
            updateSequence(updatedEntries);
        } else {
            setQueuedSequenceChanges(updatedEntries);
        }
    }

    function addEntry(): void {
        setNewEntry({
            date: new Date(periodEnd).toISOString() || new Date().toISOString(),
            nature: 'account' in worksheet ? worksheet.account?.nature : undefined,
        });
    }

    function addQuickEntry(): void {
        if (worksheet.type !== WorksheetTypeEnum.RECONCILIATION) {
            return;
        }
        setNewEntry({
            date: new Date(periodEnd).toISOString() || new Date().toISOString(),
            nature: worksheet.unreconciledCr ? NatureEnum.CREDIT : NatureEnum.DEBIT,
            amount: (worksheet.unreconciledCr || worksheet.unreconciledDr) as number,
        });
    }

    function createEntry(data: CreateWorksheetEntryRequest): void {
        createEntryQuery.mutate(
            // Convert entry amount to cents
            { entry: { ...data.entry, amount: data.entry.amount !== undefined ? parseToCents(data.entry.amount) : data.entry.amount } },
            {
                onSuccess: () => {
                    setNewEntry(undefined);
                    setViewingAttachmentsEntryId(undefined);
                },
            }
        );
    }

    function deleteEntries(): void {
        deleteEntriesQuery.mutate(
            { ids: canDeleteEntries.map((ent) => ent.id) },
            {
                onSettled: () => tableRef.current?.clear(),
                onError: (res) => {
                    toastNotification('error', 'Unable to delete entry!', res.response?.data.errorMessage);
                },
            }
        );
    }

    function updateEntries(entry: WorksheetEntryV2, data: CreateWorksheetEntryRequest): void {
        const updatedFields = data.entry;

        const { date, description, amount, attachments, nature } = updatedFields;

        const payload: Partial<WorksheetEntry> = {
            sequence: queuedSequenceChanges.find((s) => s.id === entry.id)?.fields?.sequence,
        };

        // Check attachments diff
        if (attachments !== undefined) {
            if (attachments.length !== entry.attachments.length || !attachments.every((a) => entry.attachments.includes(a))) {
                payload.attachments = attachments;
            }
        }

        // Check date diff
        if (date !== undefined) {
            if (entry.date.slice(0, 10) !== date) {
                payload.date = date;
            }
        }

        // Check description diff
        if (description !== undefined) {
            if (description !== entry.description) {
                payload.description = description || null;
            }
        }
        // Check nature diff
        if (nature !== undefined) {
            if (entry.nature !== nature) {
                payload.nature = nature;
            }
        }
        // Check amount diff
        if (amount !== undefined) {
            if (typeof entry.amount === 'number' && entry.amount !== parseToCents(amount)) {
                payload.amount = parseToCents(amount || 0);
            }
        }

        const updates: UpdateWorksheetEntriesRequest = [...queuedSequenceChanges.filter((s) => s.id !== entry.id)];

        if (Object.values(payload).some((v) => v !== undefined)) {
            updates.push({ id: entry.id, fields: payload });
        }

        if (updates.length === 0) {
            setEditingEntryId(undefined);
            setQueuedSequenceChanges([]);
            return;
        }

        updateEntriesQuery.mutate(updates, {
            onSuccess: () => {
                if (editingEntryId) {
                    setEditingEntryId(undefined);
                    setViewingAttachmentsEntryId(undefined);
                }
                setQueuedSequenceChanges([]);
            },
            onError: (res) => {
                toastNotification('error', 'Unable to update entry!', res.response?.data.errorMessage);
            },
        });
    }

    // Update entry sequences
    function updateSequence(data: { id: string; fields: Partial<WorksheetEntryV2> }[]): void {
        // @ts-expect-error Will change types later
        updateEntriesQuery.mutate(data);
    }

    return (
        <div className="mb-10">
            <div className="flex justify-between items-center mb-4">
                {!newEntry && selectedEntries.length === 0 && !editingEntryId && (
                    <>
                        <Sort
                            enableV2Query
                            size="small"
                            sortOptions={sortOptions}
                            onChange={({ queryString }): void => {
                                onSort(queryString);
                            }}
                        />

                        <div className="flex items-center gap-2">
                            {worksheet.type === WorksheetTypeEnum.RECONCILIATION && (
                                <ButtonV2
                                    leftIcon={<SparklesIcon />}
                                    size="sm"
                                    label="Add Quick Entry"
                                    type="secondary"
                                    isDisabled={readOnly}
                                    onClick={addQuickEntry}
                                />
                            )}
                            <ButtonV2
                                leftIcon={<PlusCircleIcon className="stroke-2" />}
                                size="sm"
                                label="New Entry"
                                type="secondary"
                                isDisabled={readOnly}
                                onClick={addEntry}
                            />
                        </div>
                    </>
                )}
                {(newEntry || editingEntryId) && (
                    <div className="ml-auto flex items-center gap-2">
                        <ButtonV2
                            type="secondary"
                            size="sm"
                            label="Cancel"
                            onClick={(): void => {
                                setNewEntry(undefined);
                                setEditingEntryId(undefined);
                                setQueuedSequenceChanges([]);
                            }}
                            isDisabled={createEntryQuery.isLoading || updateEntriesQuery.isLoading}
                        />
                        <ButtonV2
                            type="primary"
                            isSubmit
                            size="sm"
                            label="Save"
                            form="new-worksheet-entry"
                            isDisabled={createEntryQuery.isLoading || updateEntriesQuery.isLoading}
                            isLoading={createEntryQuery.isLoading || updateEntriesQuery.isLoading}
                        />
                    </div>
                )}
                {selectedEntries.length > 0 && (
                    <div className="ml-auto flex items-center gap-2">
                        {selectedEntries.length === 1 && (
                            <ButtonV2
                                isDisabled={readOnly}
                                type="secondary"
                                size="sm"
                                label="Edit"
                                onClick={(): void => setEditingEntryId(selectedEntries[0])}
                            />
                        )}
                        {canDeleteEntries.length > 0 && (
                            <ButtonV2
                                type="danger"
                                isSubmit
                                size="sm"
                                label={`Delete (${canDeleteEntries.length})`}
                                form="new-worksheet-entry"
                                onClick={deleteEntries}
                                isDisabled={deleteEntriesQuery.isLoading || readOnly}
                                isLoading={deleteEntriesQuery.isLoading}
                            />
                        )}
                    </div>
                )}
            </div>

            <div>
                <DataGrid
                    columns={worksheet.type === WorksheetTypeEnum.GENERIC ? genColumns : recColumns}
                    rowSizes={ROW_SIZES}
                    totalRows={entries.length}
                    disablePagination
                    emptyMessage="No entries"
                    onSelect={setSelectedEntries}
                    ref={tableRef}
                    isDraggable={!newEntry}
                    disableDragHint={isSorting ? 'Entries cannot be reordered when a sort option is applied.' : undefined}
                    disableDrag={updateEntriesQuery.isLoading || isSorting}
                    onDragging={setDragging}
                    onDrop={handleDrop}
                    onMove={handleReorder}
                >
                    {sortedEntries.map((entry) => {
                        return entry.id === editingEntryId ? (
                            <NewOrEditEntryRow
                                key={entry.id}
                                entry={entry}
                                id={entry.id}
                                mode="EDIT"
                                onSave={(data): void => updateEntries(entry, data)}
                                worksheet={worksheet}
                                dragging={isDragging}
                                disableAttachments={updateEntriesQuery.isLoading || createEntryQuery.isLoading}
                                showAttachmentWindow={viewingAttachmentsEntryId === entry.id}
                                onAttachmentWindowToggle={(open): void => {
                                    if (open) {
                                        setViewingAttachmentsEntryId(entry.id);
                                    } else {
                                        setViewingAttachmentsEntryId(undefined);
                                    }
                                }}
                            />
                        ) : (
                            <EntryRow
                                key={entry.id}
                                entry={entry}
                                id={entry.id}
                                worksheet={worksheet}
                                preventDrag={Boolean(newEntry)}
                                disabled={readOnly || isDragging}
                                disableAttachments={updateEntriesQuery.isLoading || createEntryQuery.isLoading || readOnly}
                                onAttach={(attachments): void =>
                                    updateEntries(entry, {
                                        entry: {
                                            attachments,
                                        },
                                    })
                                }
                                showAttachmentWindow={viewingAttachmentsEntryId === entry.id}
                                onAttachmentWindowToggle={(open): void => {
                                    if (open) {
                                        setViewingAttachmentsEntryId(entry.id);
                                    } else {
                                        setViewingAttachmentsEntryId(undefined);
                                    }
                                }}
                            />
                        );
                    })}
                    {newEntry && (
                        <NewOrEditEntryRow
                            id={'new'}
                            worksheet={worksheet}
                            onSave={createEntry}
                            entry={{ ...newEntry }}
                            mode="CREATE"
                            disableAttachments={updateEntriesQuery.isLoading || createEntryQuery.isLoading}
                            showAttachmentWindow={viewingAttachmentsEntryId === 'new'}
                            onAttachmentWindowToggle={(open): void => {
                                if (open) {
                                    setViewingAttachmentsEntryId('new');
                                } else {
                                    setViewingAttachmentsEntryId(undefined);
                                }
                            }}
                        />
                    )}
                </DataGrid>
                {worksheet.type === WorksheetTypeEnum.RECONCILIATION && worksheet.entries.length > 0 && (
                    <EntriesSubtotal entries={entries} />
                )}
                {worksheet.entries.length === 0 && !newEntry && <NoEntriesMessage type={worksheet.type} onClick={addEntry} />}
            </div>
        </div>
    );
}

export default EntriesTable;
